// ITIL practice panel on a ticket: reclassify type (request/incident/problem/
// change), toggle Major Incident, link an incident to a problem, and — on a
// problem — list all incidents rolled up under it.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Warning, LinkSimple, X, MagnifyingGlass, Bug, Lightning } from '@phosphor-icons/react';

const CATEGORY_OPTIONS = [
  { v: 'service_request', label: 'Service request' },
  { v: 'incident', label: 'Incident' },
  { v: 'problem', label: 'Problem' },
  { v: 'change', label: 'Change' },
];
const STATUS_TONE = { open: 'text-blue-600', in_progress: 'text-amber-600', pending: 'text-zinc-500', resolved: 'text-emerald-600', closed: 'text-zinc-400' };

export default function IncidentProblemPanel({ ticket, API, h, onUpdate, onOpenTicket }) {
  const category = ticket.category || 'service_request';
  const [problem, setProblem] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [problemResults, setProblemResults] = useState([]);
  const [q, setQ] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  const loadLinked = useCallback(async () => {
    try {
      if (category === 'problem') {
        const { data } = await axios.get(`${API}/service-desk/tickets/${ticket.ticket_id}/linked-incidents`, h());
        setIncidents(data || []);
      } else if (ticket.linked_problem_id) {
        const { data } = await axios.get(`${API}/service-desk/tickets/${ticket.linked_problem_id}`, h());
        setProblem(data);
      } else { setProblem(null); }
    } catch (_) {}
  }, [API, category, ticket.ticket_id, ticket.linked_problem_id]);
  useEffect(() => { loadLinked(); }, [loadLinked]);

  const searchProblems = async (term) => {
    try {
      const { data } = await axios.get(`${API}/service-desk/tickets`, { ...h(), params: { category: 'problem' } });
      const list = (data || []).filter(t => t.ticket_id !== ticket.ticket_id && (!term.trim() || `${t.ticket_key} ${t.title}`.toLowerCase().includes(term.toLowerCase())));
      setProblemResults(list.slice(0, 20));
    } catch (_) {}
  };

  const setCategory = (v) => onUpdate({ category: v });
  const toggleMajor = () => onUpdate({ is_major: !ticket.is_major }).then(() => toast.success(!ticket.is_major ? 'Marked as major incident' : 'Major flag cleared'));
  const linkProblem = async (p) => { await onUpdate({ linked_problem_id: p.ticket_id }); setPickerOpen(false); setQ(''); toast.success('Linked to problem'); loadLinked(); };
  const unlinkProblem = async () => { await onUpdate({ linked_problem_id: null }); setProblem(null); toast.success('Unlinked'); };

  return (
    <div data-testid="itil-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">Type</p>
      </div>
      <Select value={category} onValueChange={setCategory}>
        <SelectTrigger className="h-9 mb-3" data-testid="sd-ticket-category"><SelectValue /></SelectTrigger>
        <SelectContent>{CATEGORY_OPTIONS.map(o => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
      </Select>

      {category === 'incident' && (
        <>
          <button onClick={toggleMajor} data-testid="sd-major-toggle"
            className={`w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium mb-3 ring-1 transition-colors ${ticket.is_major ? 'bg-red-600 text-white ring-red-600' : 'bg-white text-zinc-600 ring-zinc-200 hover:bg-red-50 hover:text-red-600 hover:ring-red-200'}`}>
            <Warning size={15} weight="fill" /> {ticket.is_major ? 'Major incident — click to clear' : 'Declare major incident'}
          </button>

          <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-1.5 flex items-center gap-1"><Bug size={12} /> Problem</p>
          {problem ? (
            <div className="flex items-center gap-2 bg-purple-50/60 ring-1 ring-purple-100 rounded-md px-2.5 py-1.5" data-testid="sd-linked-problem">
              <button onClick={() => onOpenTicket(problem)} className="flex-1 min-w-0 text-left">
                <span className="font-mono text-[11px] text-purple-700 font-bold">{problem.ticket_key}</span>
                <span className="text-sm text-zinc-700 ml-1.5 truncate">{problem.title}</span>
              </button>
              <button onClick={unlinkProblem} className="text-zinc-300 hover:text-red-500" data-testid="sd-unlink-problem"><X size={13} /></button>
            </div>
          ) : (
            <Popover open={pickerOpen} onOpenChange={(o) => { setPickerOpen(o); if (o) searchProblems(''); }}>
              <PopoverTrigger asChild>
                <button className="w-full text-left text-xs text-purple-600 font-medium inline-flex items-center gap-1 hover:text-purple-700" data-testid="sd-link-problem-btn"><LinkSimple size={13} /> Link to a problem</button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 p-2">
                <div className="relative mb-2">
                  <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); searchProblems(e.target.value); }} placeholder="Search problems…" data-testid="sd-problem-search"
                    className="w-full h-8 pl-8 pr-2 rounded-md border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
                </div>
                <div className="max-h-56 overflow-auto -mx-1">
                  {problemResults.length === 0 && <p className="text-xs text-zinc-400 px-2 py-3 text-center">No problem tickets. Create one by setting a ticket's type to "Problem".</p>}
                  {problemResults.map(p => (
                    <button key={p.ticket_id} onClick={() => linkProblem(p)} data-testid={`sd-problem-option-${p.ticket_id}`}
                      className="w-full text-left px-2 py-1.5 rounded-md hover:bg-zinc-100 text-sm flex items-center gap-2">
                      <span className="font-mono text-[10px] text-purple-700 font-bold shrink-0">{p.ticket_key}</span>
                      <span className="truncate">{p.title}</span>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </>
      )}

      {category === 'problem' && (
        <>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-1.5 flex items-center gap-1"><Lightning size={12} weight="fill" /> Linked incidents ({incidents.length})</p>
          {incidents.length === 0 ? (
            <p className="text-xs text-zinc-400">No incidents linked yet. Open an incident and link it to this problem.</p>
          ) : (
            <div className="space-y-1.5" data-testid="sd-linked-incidents">
              {incidents.map(i => (
                <button key={i.ticket_id} onClick={() => onOpenTicket(i)} data-testid={`sd-incident-${i.ticket_id}`}
                  className="w-full text-left flex items-center gap-2 bg-zinc-50 hover:bg-zinc-100 rounded-md px-2.5 py-1.5 transition-colors">
                  <span className="font-mono text-[10px] text-blue-700 font-bold shrink-0">{i.ticket_key}</span>
                  <span className="text-sm text-zinc-700 truncate flex-1">{i.title}</span>
                  {i.is_major && <Warning size={12} weight="fill" className="text-red-500 shrink-0" />}
                  <span className={`text-[10px] capitalize shrink-0 ${STATUS_TONE[i.status] || 'text-zinc-400'}`}>{(i.status || '').replace('_', ' ')}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
